import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { CalendarDays, Plus, RefreshCw, Trophy } from "lucide-react";
import { ApiError } from "../../lib/api";
import { useConfirm } from "../../components/ConfirmDialog";
import { NavigationBlocker } from "../../components/NavigationBlocker";
import { useProviderWorkspace } from "../provider-workspace/ProviderWorkspaceContext";
import { resolveProviderMembershipRole } from "../provider-workspace/provider-workspace-helpers";
import { useAppPreferences } from "../../lib/preferences";
import { createTournament, listVenueTournaments, updateTournament, type TournamentRecord } from "./tournaments-api";
import { emptyTournamentForm, tournamentFormInput, tournamentFormPatch, tournamentToForm, type TournamentForm } from "./tournament-form";
import { tournamentCopy } from "./tournament-copy";

interface Editor {
  record: TournamentRecord | null;
  form: TournamentForm;
  original: TournamentForm;
  requestId: string;
}

export function TournamentsSection() {
  const { selectedVenue, summary, user, isOnline } = useProviderWorkspace();
  const { language } = useAppPreferences();
  const copy = tournamentCopy(language);
  // Retain each venue's unfinished form if the workspace venue switcher changes.
  const [editors, setEditors] = useState<Record<string, Editor | undefined>>({});
  const isDirty = Object.values(editors).some((editor) => editor && JSON.stringify(editor.form) !== JSON.stringify(editor.original));
  useEffect(() => {
    if (!isDirty) return;
    const guard = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [isDirty]);
  const membershipRole = resolveProviderMembershipRole(summary, user);
  return (
    <div className="grid gap-5" dir={language === "ar" ? "rtl" : "ltr"}>
      <NavigationBlocker isDirty={isDirty} />
      <header className="grid max-w-3xl gap-1">
        <p className="ops-kicker">{selectedVenue?.name ?? copy.eyebrow}</p>
        <h1 className="ops-page-title">{copy.title}</h1>
        <p className="ops-copy text-sm leading-6">{copy.intro}</p>
      </header>
      {!selectedVenue ? <div className="ops-empty">{copy.noVenue}</div> : (
        <VenueTournaments
          key={selectedVenue.id}
          canManage={membershipRole === "owner" || membershipRole === "manager"}
          canPublish={selectedVenue.approval_status === "approved" && selectedVenue.is_active}
          editor={editors[selectedVenue.id] ?? null}
          isOnline={isOnline}
          language={language}
          setEditor={(editor) => setEditors((current) => ({ ...current, [selectedVenue.id]: editor ?? undefined }))}
          timezone={selectedVenue.timezone}
          venueId={selectedVenue.id}
        />
      )}
    </div>
  );
}

function VenueTournaments({ venueId, timezone, canManage, canPublish, isOnline, language, editor, setEditor }: {
  venueId: string;
  timezone: string;
  canManage: boolean;
  canPublish: boolean;
  isOnline: boolean;
  language: string;
  editor: Editor | null;
  setEditor: (editor: Editor | null) => void;
}) {
  const copy = tournamentCopy(language);
  const [events, setEvents] = useState<TournamentRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<"draftSaved" | "saved" | "sentForReview" | "withdrawn" | "cancelledNotice" | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const pendingRef = useRef(false);
  const [total, setTotal] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const moreController = useRef<AbortController | null>(null);
  const [revision, setRevision] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const { confirm, dialog } = useConfirm();
  const reload = useCallback(() => setRevision((current) => current + 1), []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    void listVenueTournaments(venueId, controller.signal)
      .then((response) => { if (!controller.signal.aborted) { setEvents(response.tournaments); setTotal(response.total); setLoadedCount(response.tournaments.length); } })
      .catch(() => { if (!controller.signal.aborted) setLoadError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { controller.abort(); moreController.current?.abort(); };
  }, [venueId, revision, isOnline]);
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const locale = language === "ar" ? "ar-EG-u-nu-latn" : "en-GB-u-nu-latn";
  const dayPart = (value: string) => new Intl.DateTimeFormat(locale, { timeZone: timezone, weekday: "short", day: "numeric", month: "short", year: new Date(value).getFullYear() === new Date(now).getFullYear() ? undefined : "numeric" }).format(new Date(value));
  const timePart = (value: string) => new Intl.DateTimeFormat(locale, { timeZone: timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(value));
  // "Thu 1 Oct · 20:00 – 00:00" for one evening, both dates when it runs longer than a day.
  const eventWhen = (event: TournamentRecord) => {
    const hours = (new Date(event.ends_at).getTime() - new Date(event.starts_at).getTime()) / 3_600_000;
    return hours < 24
      ? `${dayPart(event.starts_at)} · ${timePart(event.starts_at)} – ${timePart(event.ends_at)}`
      : `${dayPart(event.starts_at)} ${timePart(event.starts_at)} – ${dayPart(event.ends_at)} ${timePart(event.ends_at)}`;
  };
  const eventState = (event: TournamentRecord) => {
    if (event.status === "cancelled") return "cancelled";
    if (event.status === "draft") return "draft";
    if (new Date(event.ends_at).getTime() <= now) return "ended";
    if (event.review_status === "pending") return "review";
    if (event.review_status === "rejected") return "changes";
    return new Date(event.starts_at).getTime() <= now ? "live" : "published";
  };
  async function loadMore() {
    if (loadingMore || !events || loading) return;
    const controller = new AbortController();
    moreController.current = controller;
    setLoadingMore(true);
    try {
      const response = await listVenueTournaments(venueId, controller.signal, loadedCount);
      if (controller.signal.aborted) return;
      setEvents((current) => [...new Map([...(current ?? []), ...response.tournaments].map((event) => [event.id, event])).values()]);
      setTotal(response.total);
      setLoadedCount((current) => current + response.tournaments.length);
      setLoadError(false);
    } catch { if (!controller.signal.aborted) setLoadError(true); }
    finally { if (!controller.signal.aborted) setLoadingMore(false); }
  }

  async function discardEditor() {
    if (!editor || JSON.stringify(editor.form) === JSON.stringify(editor.original)) return true;
    return confirm({ title: copy.discardTitle, description: copy.discardBody, confirmButtonLabel: copy.discard });
  }
  async function openEditor(record: TournamentRecord | null) {
    if (pendingRef.current || !(await discardEditor())) return;
    const form = record ? tournamentToForm(record) : { ...emptyTournamentForm };
    setEditor({ record, form, original: form, requestId: crypto.randomUUID() });
    setError(null);
    setNotice(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
      formRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    });
  }
  async function save(status: TournamentRecord["status"]) {
    if (!editor || pendingRef.current || !isOnline) return;
    const result = tournamentFormInput(editor.form, timezone, status, now, editor.record);
    setNotice(null);
    if (result.error) { setError(copy[`error_${result.error}`]); return; }
    pendingRef.current = true;
    setPending("editor");
    setError(null);
    try {
      const saved = editor.record ? await updateTournament(venueId, editor.record.id, tournamentFormPatch(editor.form, editor.original, editor.record, result.input)) : await createTournament(venueId, result.input, editor.requestId);
      setEvents((current) => [saved, ...(current ?? []).filter((event) => event.id !== saved.id)]);
      setEditor(null);
      const sentForReview = saved.review_status === "pending" && editor.record?.review_status !== "pending";
      setNotice(sentForReview ? "sentForReview" : status === "draft" ? "draftSaved" : "saved");
      reload();
    } catch (caught) {
      setError(caught instanceof ApiError && caught.status > 0 ? `${caught.detail} ${copy.unsaved}` : copy.saveError);
    } finally { pendingRef.current = false; setPending(null); }
  }
  async function cancel(event: TournamentRecord) {
    if (pendingRef.current || !isOnline) return;
    if (!(await confirm({ title: copy.cancelTitle, description: copy.cancelBody, confirmButtonLabel: copy.cancel, cancelButtonLabel: copy.keep }))) return;
    pendingRef.current = true;
    setPending(event.id);
    setError(null);
    setNotice(null);
    try {
      const saved = await updateTournament(venueId, event.id, { status: "cancelled", expected_updated_at: event.updated_at });
      setEvents((current) => (current ?? []).map((item) => item.id === saved.id ? saved : item));
      // Discarding a different event's draft would lose unrelated work.
      if (editor?.record?.id === event.id) setEditor(null);
      setNotice("cancelledNotice");
      reload();
    } catch (caught) { setError(caught instanceof ApiError && caught.status > 0 ? caught.detail : copy.saveError); }
    finally { pendingRef.current = false; setPending(null); }
  }
  // Players never saw a tournament that was not approved, so it can go back to the drafts.
  async function withdraw(event: TournamentRecord) {
    if (pendingRef.current || !isOnline) return;
    pendingRef.current = true;
    setPending(event.id);
    setError(null);
    setNotice(null);
    try {
      const saved = await updateTournament(venueId, event.id, { status: "draft", expected_updated_at: event.updated_at });
      setEvents((current) => (current ?? []).map((item) => item.id === saved.id ? saved : item));
      if (editor?.record?.id === event.id) setEditor(null);
      setNotice("withdrawn");
      reload();
    } catch (caught) { setError(caught instanceof ApiError && caught.status > 0 ? caught.detail : copy.saveError); }
    finally { pendingRef.current = false; setPending(null); }
  }
  function field(key: keyof TournamentForm, label: string, options: { type?: string; maxLength?: number; required?: boolean; publishRequired?: boolean; hint?: string; area?: boolean } = {}) {
    const id = `tournament-${key}`;
    const props = {
      id, name: key, className: "ops-input w-full", value: editor?.form[key] ?? "", maxLength: options.maxLength,
      required: options.required, disabled: Boolean(pending), "aria-describedby": options.hint ? `${id}-hint` : undefined,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (editor) setEditor({ ...editor, form: { ...editor.form, [key]: event.target.value } });
      },
    };
    return <div className="grid min-w-0 gap-2 text-sm text-slate-200">
      <label htmlFor={id}>{label}{!options.required ? <span aria-hidden="true" className="ms-2 text-xs text-slate-500">{options.publishRequired ? copy.requiredToPublish : copy.optional}</span> : null}</label>
      {options.area ? <textarea {...props} rows={key === "rules" ? 4 : 3} /> : <input {...props} type={options.type ?? "text"} autoComplete="off" />}
      {options.hint ? <span className="text-xs leading-5 text-slate-400" id={`${id}-hint`}>{options.hint}</span> : null}
    </div>;
  }

  // Anything gaamed sent back comes first, then what's coming up soonest, then the past.
  const attention = { changes: 0, review: 1, draft: 2, live: 1, published: 1 } as Record<string, number>;
  const upcoming = (events ?? []).filter((event) => !["ended", "cancelled"].includes(eventState(event)))
    .sort((a, b) => (attention[eventState(a)] ?? 1) - (attention[eventState(b)] ?? 1) || a.starts_at.localeCompare(b.starts_at));
  const past = (events ?? []).filter((event) => ["ended", "cancelled"].includes(eventState(event)))
    .sort((a, b) => b.starts_at.localeCompare(a.starts_at));
  const groups = [{ key: "upcoming", title: copy.upcoming, events: upcoming }, { key: "past", title: copy.past, events: past }];

  return <>
    {dialog}
    {!isOnline ? <div className="ops-banner" data-tone="warning" role="status">{copy.offline}</div> : null}
    {!canManage ? <div className="ops-banner" data-tone="info">{copy.readOnly}</div> : null}
    {!canPublish ? <div className="ops-banner" data-tone="warning">{copy.approval}</div> : null}
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-slate-400">{copy.all}{events ? ` · ${total}` : ""}</p>
      <div className="flex gap-2">
        <button aria-label={copy.refresh} className="ops-button" data-variant="secondary" disabled={loading || loadingMore || Boolean(pending)} onClick={reload} type="button"><RefreshCw aria-hidden size={16} /><span className="sr-only sm:not-sr-only">{copy.refresh}</span></button>
        {canManage ? <button className="ops-button" data-variant="primary" disabled={!isOnline || Boolean(pending)} onClick={() => void openEditor(null)} type="button"><Plus aria-hidden size={16} />{copy.add}</button> : null}
      </div>
    </div>
    {notice ? <div className="ops-banner" data-tone="success" role="status">{copy[notice]}</div> : null}
    {error ? <div className="ops-banner outline-none" data-tone="danger" ref={errorRef} role="alert" tabIndex={-1}>{error}</div> : null}
    {editor && canManage ? (
      <form className="ops-surface grid gap-5" ref={formRef} onSubmit={(event: FormEvent) => { event.preventDefault(); void save(editor.record?.status ?? "draft"); }}>
        <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-lg font-semibold text-white">{editor.record ? copy.editing : copy.creating}</h2><button className="ops-button" data-variant="secondary" disabled={Boolean(pending)} onClick={() => void discardEditor().then((discard) => { if (discard) { setEditor(null); setError(null); } })} type="button">{copy.close}</button></div>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("name", copy.name, { required: true, maxLength: 120 })}
          {field("game", copy.game, { required: true, maxLength: 100 })}
          {field("starts_at", copy.start, { required: true, type: "datetime-local" })}
          {field("ends_at", copy.end, { required: true, type: "datetime-local" })}
        </div>
        <p className="-mt-2 text-xs text-slate-400">{copy.timezone} <bdi>{timezone}</bdi></p>
        {editor.record?.review_status === "approved" && editor.record.status === "published" ? <div className="ops-banner" data-tone="info">{copy.reReview}</div> : null}
        {editor.record?.review_status === "rejected" && editor.record.review_note ? <div className="ops-banner" data-tone="warning"><strong>{copy.changesTitle}</strong> <span className="whitespace-pre-wrap">{editor.record.review_note}</span></div> : null}
        {field("description", copy.description, { area: true, maxLength: 2000 })}
        {field("rules", copy.rules, { area: true, maxLength: 4000, hint: copy.rulesHint })}
        {field("registration_instructions", copy.entry, { area: true, maxLength: 2000, publishRequired: true, hint: copy.entryHint })}
        {field("contact_phone", copy.phone, { type: "tel", maxLength: 32, hint: copy.phoneHint })}
        <div className="rounded-xl border border-white/10 bg-white/[0.025] p-4 text-xs leading-6 text-slate-400">{copy.handoff}</div>
        <div className="flex flex-wrap items-center gap-3">
          <button className="ops-button" data-variant={editor.record?.status === "published" ? "primary" : "secondary"} disabled={Boolean(pending) || !isOnline} type="submit">{pending === "editor" ? copy.saving : editor.record?.status === "published" && editor.record.review_status === "rejected" ? copy.saveAndResend : editor.record ? copy.saveChanges : copy.save}</button>
          {editor.record?.status !== "published" ? <button className="ops-button" data-variant="primary" disabled={Boolean(pending) || !isOnline || !canPublish} onClick={() => void save("published")} type="button">{copy.publish}</button> : null}
          <p className="text-xs text-slate-400">{copy.publishHelp}</p>
        </div>
      </form>
    ) : null}
    {loading && !events ? <div aria-busy="true" className="ops-empty">{copy.loading}</div> : null}
    {loadError ? <div className="ops-banner flex flex-wrap items-center justify-between gap-3" data-tone="danger" role="alert"><span>{copy.failed}</span><button className="ops-button" data-variant="secondary" disabled={loading} onClick={reload} type="button">{copy.retry}</button></div> : null}
    {events?.length === 0 && !loading && !loadError && !editor ? <section className="ops-surface grid justify-items-center gap-4 py-12 text-center"><Trophy aria-hidden className="text-[var(--color-accent)]" size={38} /><h2 className="text-xl font-semibold text-white">{copy.noEvents}</h2><p className="max-w-md text-sm leading-6 text-slate-400">{copy.noEventsBody}</p>{canManage ? <button className="ops-button" data-variant="primary" disabled={!isOnline} onClick={() => void openEditor(null)} type="button">{copy.add}</button> : null}</section> : null}
    {groups.map((group) => group.events.length === 0 ? null : <section aria-label={group.title} className="grid gap-3" key={group.key}>
      {groups.filter((item) => item.events.length > 0).length > 1 ? <h2 className="ops-label">{group.title} · {group.events.length}</h2> : null}
      {group.events.map((event) => {
        const state = eventState(event);
        const started = new Date(event.starts_at).getTime() <= now;
        return <article className="ops-surface grid gap-4" key={event.id}>
          <div className="flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-start">
            <div className="grid min-w-0 flex-1 gap-2"><div className="flex flex-wrap items-center gap-2"><p className="text-xs font-semibold text-[var(--color-accent)]">{event.game}</p><span className="ops-chip" data-tone={state === "published" || state === "live" ? "success" : state === "changes" ? "danger" : state === "cancelled" || state === "review" ? "warning" : "info"}>{copy[state]}</span></div><h2 className="break-words text-lg font-semibold text-white">{event.name}</h2><p className="flex items-center gap-2 text-sm text-slate-300"><CalendarDays aria-hidden className="shrink-0" size={15} /><span>{eventWhen(event)}</span></p></div>
            {canManage ? <div className="flex flex-wrap gap-2">{!started || event.status === "draft" || state === "live" ? <button className="ops-button" data-variant="secondary" disabled={Boolean(pending) || !isOnline} onClick={() => void openEditor(event)} type="button">{copy.edit}</button> : null}{event.status === "published" && new Date(event.ends_at).getTime() > now ? (
              !event.approved_at
                ? <button className="ops-button" data-variant="secondary" disabled={Boolean(pending) || !isOnline} onClick={() => void withdraw(event)} type="button">{pending === event.id ? copy.saving : copy.withdraw}</button>
                : <button className="ops-button" data-variant="danger" disabled={Boolean(pending) || !isOnline} onClick={() => void cancel(event)} type="button">{pending === event.id ? copy.saving : copy.cancel}</button>
            ) : null}</div> : null}
          </div>
          {state === "ended" || state === "live" ? <p className="text-xs text-slate-400">{state === "ended" ? copy.endedHelp : copy.liveHelp}</p> : null}
          {state === "review" ? <p className="text-xs text-slate-400">{copy.reviewHelp}</p> : null}
          {state === "changes" ? <div className="ops-banner grid gap-1" data-tone="warning">{event.review_note ? <p><strong>{copy.changesTitle}</strong> <span className="whitespace-pre-wrap break-words">{event.review_note}</span></p> : null}<p className="text-xs">{copy.changesHelp}</p></div> : null}
          {event.registration_instructions ? <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-400">{event.registration_instructions}</p> : null}
        </article>;
      })}
    </section>)}
    {events && loadedCount < total ? <button className="ops-button justify-self-center" data-variant="secondary" disabled={loadingMore || loading || !isOnline} onClick={() => void loadMore()} type="button">{loadingMore ? copy.loading : copy.more}</button> : null}
  </>;
}
